import {tuple} from "~/types";
import {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {
    delAccessToken,
    getAccessToken, getAccessTokenExpiresIn,
    onAccessTokenChanged,
    onAccessTokenExpired,
    setAccessToken, AccessToken
} from "~/auth/functions";
import {
    DeleteAccessTokenResult,
    DeleteAccessTokenVars,
    DeleteAccessTokenDocument,
    RefreshAccessTokenResult,
    RefreshAccessTokenVars,
    RefreshAccessTokenDocument,
    CreateAccessTokenResult,
    CreateAccessTokenVars,
    CreateAccessTokenDocument,
    GetAboutMeFromAVResult,
    GetAboutMeFromAVVars,
    GetAboutMeFromAVDocument,
    GetAboutMeFromERPResult,
    GetAboutMeFromERPVars,
    GetAboutMeFromERPDocument, PingAVEndpointDocument, PingERPEndpointDocument
} from "~/auth/queries";
import {useErrorHandler} from "~/components/ErrorHandling";
import {
    ApolloError,
    isApolloError,
    useApolloClient,
    useMutation,
    useQuery
} from "@apollo/client";
import {LogicError} from "~/errors";
import {isForbiddenError} from "~/apollo/errors";
import {useThrottleCallback} from "@react-hook/throttle";
import {useUnsubscribePush} from "~/notifications/hooks";


function deserializeAccessToken(data: RefreshAccessTokenResult["refreshAccessToken"] | CreateAccessTokenResult["createAccessToken"]): AccessToken {
    if (!data) {
        return null;
    }
    return {
        accessToken: data.access_token,
        expiresAt: new Date(Date.now() + data.expires_in * 1000),
    };
}


const AccessTokenContext = createContext<AccessToken>(null);


export function useAccessTokenProvider() {
    return function AccessTokenProvider(props) {
        const client = useApolloClient();
        const [mounted, setMounted] = useState(false);
        const [accessToken, setAccessToken] = useState<AccessToken>(getAccessToken());

        useEffect(() => {
            setMounted(true);
        }, [])

        // update access token on change
        useEffect(() => onAccessTokenChanged(accessToken => {
            setAccessToken(accessToken);
        }), [setAccessToken]);

        // update access token on expired
        useEffect(() => onAccessTokenExpired(() => {
            setAccessToken(accessToken);
        }), [setAccessToken]);

        useEffect(() => {
            // prevent initial call
            if (!mounted) {
                return
            }
            if (getAccessTokenExpiresIn(accessToken) <= 0) {
                client.resetStore();
            }
        }, [accessToken, client]);

        return (
            <AccessTokenContext.Provider {...props} value={accessToken}/>
        );
    }
}


export function useAccessToken() {
    return useContext(AccessTokenContext);
}


export function useLogin() {
    function isInvalidCredentials(error: Error) {
        return isApolloError(error) && error.graphQLErrors?.find(x => x.extensions.code == "invalid_grant");
    }

    const handleError = useErrorHandler();
    const [execute, result] = useMutation<CreateAccessTokenResult, CreateAccessTokenVars>(CreateAccessTokenDocument, {
        notifyOnNetworkStatusChange: true,
    });
    const {data, loading, called} = result;
    let {error} = result;
    let success = called ? !error && data && !loading : null;

    if (error && isInvalidCredentials(error)) {
        error = null;
        success = false;
    }

    const login = (username: string, password: string) => {
        execute({
            variables: {
                input: {
                    username,
                    password
                }
            }
        }).catch(e => {
            if (!isInvalidCredentials(e)) {
                throw e;
            }
        });
    }

    useEffect(() => {
        if (error) {
            handleError(error);
        }
    }, [error]);

    useEffect(() => {
        if (data) {
            setAccessToken(deserializeAccessToken(data?.createAccessToken));
        }
    }, [data]);

    return tuple(login, {error, loading, called, success});
}


export function useRefreshAccessToken() {
    function isTokenExpiredError(error: ApolloError) {
        if (error?.networkError instanceof LogicError && error?.networkError.code == "not_found") {
            return true;
        }
        for (const e of error.graphQLErrors) {
            if (e.extensions.code == "not_found") {
                return true;
            }
        }
    }

    const accessToken = useAccessToken();
    const [mutation, result] = useMutation<RefreshAccessTokenResult, RefreshAccessTokenVars>(RefreshAccessTokenDocument, {
        notifyOnNetworkStatusChange: true,
        variables: {
            input: {}
        }
    });
    const {loading, called} = result;

    let {error, data} = result;
    if (error && isTokenExpiredError(error)) {
        error = null;
        data = null;
    }

    const execute = useThrottleCallback(useCallback(() => {
        if (accessToken && getAccessTokenExpiresIn(accessToken) > 0) {
            mutation().catch(error => {
                if (!isTokenExpiredError(error)) {
                    throw error;
                }
            });
        }
    }, [accessToken]), 1 / 30, true);

    useEffect(() => {
        if (error || loading || !called) {
            return
        }
        if (data) {
            setAccessToken(deserializeAccessToken(data?.refreshAccessToken));
        } else {
            delAccessToken();
        }
    }, [data, loading, error, called]);

    return tuple(execute);
}


export function useLogout() {
    const handleError = useErrorHandler();
    const [unsubscribe] = useUnsubscribePush();
    const [mutation, {
        called,
        loading,
        data,
        error
    }] = useMutation<DeleteAccessTokenResult, DeleteAccessTokenVars>(DeleteAccessTokenDocument);
    const success = !loading && called;

    const logout = async () => {
        await unsubscribe();
        await mutation();
    }

    useEffect(() => {
        if (success) {
            delAccessToken();
        }
    }, [success]);

    useEffect(() => {
        if (error) {
            handleError(error);
        }
    }, [error])

    return tuple(logout, {called, loading, error});
}


export function useUserAuthenticated() {
    const accessToken = useAccessToken();
    return getAccessTokenExpiresIn(accessToken) > 0;
}


type CurrentUserContextValue = [
        GetAboutMeFromAVResult["aboutMeAV"]["aboutMe"] | GetAboutMeFromERPResult["aboutMeERP"]["aboutMe"], boolean
];
const CurrentUserContext = createContext<CurrentUserContextValue>(null);

export function useCurrentUserProviderValue(): CurrentUserContextValue {
    const handleError = useErrorHandler();
    const authenticated = useUserAuthenticated();
    const av = useQuery<GetAboutMeFromAVResult, GetAboutMeFromAVVars>(GetAboutMeFromAVDocument, {
        notifyOnNetworkStatusChange: true,
        skip: !authenticated,
    });
    const erp = useQuery<GetAboutMeFromERPResult, GetAboutMeFromERPVars>(GetAboutMeFromERPDocument, {
        notifyOnNetworkStatusChange: true,
        skip: !authenticated,
    });

    const error = av.error && erp.error;
    const data = erp.data?.aboutMeERP?.aboutMe || av.data?.aboutMeAV?.aboutMe;
    const loading = (av.loading || erp.loading) && !data;

    useEffect(() => {
        if (error) {
            handleError(error);
            if (!isForbiddenError(error)) {
                const timeoutID = setTimeout(() => {
                    av.refetch();
                    erp.refetch();
                }, 5000)
                return () => clearTimeout(timeoutID);
            }
        }
    }, [error, av.refetch, erp.refetch])

    return tuple(loading ? undefined : data, loading || error != null);
}


export function useCurrentUserProvider() {
    return function CurrentUserProvider(props) {
        const value = useCurrentUserProviderValue();
        return (
            <CurrentUserContext.Provider {...props} value={value}/>
        )
    }
}

export function useCurrentUser() {
    return useContext(CurrentUserContext);
}


export function useCheckEndpoints(endpoints: ("av" | "erp")[]) {
    const av = useQuery(PingAVEndpointDocument, {
        fetchPolicy: "no-cache",
        notifyOnNetworkStatusChange: true,
        skip: !endpoints.includes("av")
    });
    const erp = useQuery(PingERPEndpointDocument, {
        fetchPolicy: "no-cache",
        notifyOnNetworkStatusChange: true,
        skip: !endpoints.includes("erp")
    });
    const results = {
        "erp": erp,
        "av": av,
    }

    const loading = endpoints.map(x => results[x].loading).includes(true);
    const error = endpoints.map(x => results[x].error).filter(Boolean)[0];
    const ok = !error && !loading;
    return {loading, error, ok};
}